/**
 * Shared design contract for every DevTools surface (panel chrome, inspector
 * cards, contributed sections). Injected CSS strings cannot import the design
 * system, so this module is the single place that maps host shadcn bridge
 * tokens to the panel's own `--dt-*` vocabulary:
 *
 * - **Themed host** (shell, dev-shell): each `--dt-*` resolves to the host's
 *   bridge token, so the panel follows the active light/dark theme.
 * - **Unthemed host**: every token carries a dark-HUD fallback — the panel
 *   renders as a readable dark overlay instead of transparent/unstyled.
 *
 * Surface stylesheets consume `var(--dt-*)` only; per-property `var(--token,
 * fallback)` pairs are forbidden in DevTools CSS (they drift, and a missed
 * fallback renders transparent on unthemed hosts).
 *
 * Type ramp is fixed at three stops (13/12/11px) and radii at two (6/4px),
 * independent of the host's `--radius`: the panel is a dense diagnostic HUD,
 * not a product surface, and internal consistency outranks host roundness.
 */
const DT_TOKEN_DECLS = /* css */ `
  --dt-bg: var(--card, #17181c);
  --dt-surface: var(--background, #101114);
  --dt-fg: var(--foreground, #e7e9ec);
  --dt-muted: var(--muted, #23252b);
  --dt-muted-fg: var(--muted-foreground, #9aa1ab);
  --dt-border: var(--border, #34363d);
  --dt-accent: var(--primary, #3b82f6);
  --dt-accent-fg: var(--primary-foreground, #ffffff);
  --dt-ring: var(--ring, #3b82f6);
  --dt-destructive: var(--destructive, #e5484d);
  --dt-font-mono: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  --dt-radius: 6px;
  --dt-radius-sm: 4px;
  --dt-text-base: 0.8125rem;
  --dt-text-sm: 0.75rem;
  --dt-text-xs: 0.6875rem;
  --dt-ease: var(--ax-transition-easing-standard, cubic-bezier(0.22, 0.61, 0.36, 1));
  --dt-dur-slow: 240ms;
  --dt-dur: 180ms;
  --dt-dur-fast: 120ms;`;

/**
 * Emit the `--dt-*` token block scoped to the given root selectors, plus the
 * reduced-motion override that zeroes every duration token. All DevTools
 * motion rides `--dt-dur*`, so this one media block silences the whole
 * surface — individual rules never need their own reduced-motion variants.
 *
 * Each independently-injectable root (a section may mount without the panel,
 * e.g. dev-shell embeds FeatureFlagsSection directly) declares its own scope.
 */
export function dtTokenScope(...selectors: string[]): string {
  const scope = selectors.join(',\n');
  return /* css */ `
${scope} {
${DT_TOKEN_DECLS}
}

@media (prefers-reduced-motion: reduce) {
  ${scope} {
    --dt-dur-slow: 0ms;
    --dt-dur: 0ms;
    --dt-dur-fast: 0ms;
  }
}
`;
}

/**
 * Unified focus indicator for every interactive element in DevTools surfaces.
 * Always on `--dt-ring`; offset varies by geometry (inset for flush elements
 * like tabs, outset for free-standing buttons).
 */
export function dtFocusRing(selector: string, offset = '2px'): string {
  return /* css */ `
${selector}:focus-visible {
  outline: 2px solid var(--dt-ring);
  outline-offset: ${offset};
}
`;
}
